import { getLocale, history, setLocale, useIntl, useModel } from '@umijs/max';
import { Menu, Modal, Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import {
  BulbOutlined,
  GlobalOutlined,
  LogoutOutlined,
  SettingOutlined,
} from '@/components/icon/tabler';
import { useThemeMode } from '@/components/ThemeRoot';
import { useDangerConfirm } from '@/hooks';
import Settings from '@/pages/account/settings';
import { logout } from '@/services/auth/api';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  children?: React.ReactNode;
};

const SUBMENU_GAP = 8;
const SUBMENU_POPUP_OFFSET: [number, number] = [SUBMENU_GAP, 0];

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};

  return (
    <span style={{ color: 'var(--ant-color-text)' }}>{currentUser?.name}</span>
  );
};

const useStyles = createStyles(({ token, css }) => {
  return {
    action: {
      display: 'inline-flex',
      height: 32,
      marginLeft: 'auto',
      alignItems: 'center',
      gap: 6,
      padding: '0 10px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      border: `1px solid ${token.colorBorder}`,
      backgroundColor: token.colorFillTertiary,
      transition: 'background-color 0.15s, border-color 0.15s',
      '&:hover': {
        backgroundColor: token.colorFillSecondary,
        borderColor: token.colorBorderSecondary,
      },
    },
    panel: css`
      width: 240px;
      max-width: calc(100vw - 24px);
      padding: 6px;
      background-color: ${token.colorBgElevated};
      border-radius: ${token.borderRadiusLG}px;
      border: 1px solid ${token.colorBorderOverlay ?? token.colorBorder};
      box-shadow: ${token.boxShadowPanel ?? token.boxShadowSecondary};
      :is(.ant-menu, .ant-dropdown-menu),
      :is(.ant-menu-item-group-title, .ant-dropdown-menu-item-group-title),
      :is(.ant-menu-sub, .ant-dropdown-menu-sub),
      :is(.ant-menu-submenu, .ant-dropdown-menu-submenu) > :is(.ant-menu, .ant-dropdown-menu) {
        background-color: transparent;
      }
      :is(.ant-menu-item, .ant-dropdown-menu-item),
      :is(.ant-menu-submenu-title, .ant-dropdown-menu-submenu-title) {
        margin: 2px 0 !important;
        width: 100% !important;
        border-radius: ${token.borderRadius}px !important;
      }
    `,
    submenuPopup: css`
      border: 1px solid ${token.colorBorderOverlay ?? token.colorBorder};
      box-shadow: ${token.boxShadowPopup ?? token.boxShadowSecondary};
      border-radius: ${token.borderRadiusLG}px !important;
      overflow: hidden;

      :is(.ant-menu, .ant-dropdown-menu) {
        padding: 6px !important;
        border-radius: ${token.borderRadiusLG}px !important;
        overflow: hidden;
        background: ${token.colorBgPopup ?? token.colorBgElevated} !important;
        box-shadow: none !important;
      }

      :is(.ant-menu-item, .ant-dropdown-menu-item) {
        margin: ${token.lineWidth}px 0 !important;
        border-radius: ${token.borderRadius}px !important;
      }
    `,
    menuFlat: {
      border: 'none',
      backgroundColor: 'transparent',
    },
    flushModalContent: css`
      padding: 0 !important;
      overflow: hidden;
    `,
    flushModalBody: css`
      padding: 0 !important;
      overflow: hidden;
      height: 58vh;
    `,
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  children,
}) => {
  const { styles } = useStyles();
  const intl = useIntl();
  const { initialState, setInitialState } = useModel('@@initialState');
  const currentLocale = getLocale();
  const { preference: themePreference, setPreference: setThemePreference } =
    useThemeMode();
  const confirm = useDangerConfirm();

  const [open, setOpen] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const loginOut = async () => {
    await logout();
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    const redirect = urlParams.get('redirect');
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace(`/user/login?${searchParams.toString()}`);
    }
  };
  const confirmLogout = () => {
    confirm({
      tone: 'primary',
      title: intl.formatMessage({
        id: 'component.logout.confirmTitle',
        defaultMessage: '退出登录?',
      }),
      description: intl.formatMessage({
        id: 'component.logout.confirmDesc',
        defaultMessage: '确认退出当前账号吗?',
      }),
      okText: intl.formatMessage({
        id: 'menu.account.logout',
        defaultMessage: '退出登录',
      }),
      onOk: async () => {
        flushSync(() => {
          setInitialState((s: typeof initialState) => ({
            ...(s ?? {}),
            currentUser: undefined,
          }));
        });
        await loginOut();
      },
    });
  };

  if (!initialState) {
    return (
      <span className={styles.action}>
        <Spin size="small" style={{ marginLeft: 8, marginRight: 8 }} />
      </span>
    );
  }

  const { currentUser } = initialState;
  if (!currentUser?.name) {
    return (
      <span className={styles.action}>
        <Spin size="small" style={{ marginLeft: 8, marginRight: 8 }} />
      </span>
    );
  }

  const menuItems = [
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: intl.formatMessage({
        id: 'menu.account.settings',
        defaultMessage: '个人设置',
      }),
    },
    {
      key: 'lang',
      icon: <GlobalOutlined />,
      popupClassName: styles.submenuPopup,
      popupOffset: SUBMENU_POPUP_OFFSET,
      label: intl.formatMessage({
        id: 'menu.account.lang',
        defaultMessage: '语言',
      }),
      children: [
        {
          key: 'lang:zh-CN',
          label: '简体中文',
          disabled: currentLocale === 'zh-CN',
        },
        {
          key: 'lang:en-US',
          label: 'English',
          disabled: currentLocale === 'en-US',
        },
      ],
    },
    {
      key: 'theme',
      icon: <BulbOutlined />,
      popupClassName: styles.submenuPopup,
      popupOffset: SUBMENU_POPUP_OFFSET,
      label: intl.formatMessage({
        id: 'menu.account.theme',
        defaultMessage: '外观',
      }),
      children: [
        {
          key: 'theme:system',
          label: intl.formatMessage({
            id: 'menu.account.theme.system',
            defaultMessage: '跟随系统',
          }),
          disabled: themePreference === 'system',
        },
        {
          key: 'theme:light',
          label: intl.formatMessage({
            id: 'menu.account.theme.light',
            defaultMessage: '浅色模式',
          }),
          disabled: themePreference === 'light',
        },
        {
          key: 'theme:dark',
          label: intl.formatMessage({
            id: 'menu.account.theme.dark',
            defaultMessage: '深色模式',
          }),
          disabled: themePreference === 'dark',
        },
      ],
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({
        id: 'menu.account.logout',
        defaultMessage: '退出登录',
      }),
    },
  ];

  const handleMenuClick = (info: { key: string }) => {
    if (info.key.startsWith('lang:')) {
      const locale = info.key.slice('lang:'.length);
      setOpen(false);

      setLocale(locale, false);
      return;
    }
    if (info.key.startsWith('theme:')) {
      const next = info.key.slice('theme:'.length);
      setOpen(false);
      if (next === 'light' || next === 'dark' || next === 'system') {
        setThemePreference(next);
      }
      return;
    }
    setOpen(false);
    if (info.key === 'logout') {
      confirmLogout();
      return;
    }
    if (info.key === 'settings') {
      setSettingsOpen(true);
    }
  };

  return (
    <>
      <HeaderDropdown
        open={open}
        onOpenChange={setOpen}
        placement="bottomRight"
        trigger={['hover']}
        dropdownRender={() => (
          <div className={styles.panel}>
            <Menu
              className={styles.menuFlat}
              selectedKeys={[]}
              items={menuItems}
              onClick={handleMenuClick}
            />
          </div>
        )}
      >
        {children}
      </HeaderDropdown>
      <Modal
        classNames={{
          container: styles.flushModalContent,
          body: styles.flushModalBody,
        }}
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        footer={null}
        width={1180}
        centered
        destroyOnHidden
      >
        <Settings />
      </Modal>
    </>
  );
};
